// src/pages/Analytics.js
import { useStructuredData } from '../hooks/useStructuredData';
import { useSEO } from '../hooks/useSEO';
import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
    ArcElement,
    PointElement,
    LineElement,
    Filler
} from 'chart.js';

import { Doughnut, Line } from 'react-chartjs-2';
import { 
    BarChart3, 
    TrendingUp, 
    Target, 
    Zap, 
    Users, 
    Calendar, 
    Tag, 
    Settings, 
    Cpu, 
    Eye, 
    Brain, 
    Gauge, 
    Activity, 
    Clock, 
    PieChart, 
    LineChart,
    RefreshCw,
    AlertCircle,
    Database,
    Sparkles,
    ChevronRight,
    Globe
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import './Analytics.css';


// Register Chart.js components
ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
    ArcElement,
    PointElement,
    LineElement,
    Filler
);

function Analytics() {

  // SEO Configuration for Analytics page
  const seoConfig = {
    title: "Analytics & Insights - Trendphile | AI-Powered Prediction Platform",
    description: "Advanced analytics dashboard for trend predictions on our AI-powered prediction platform. Track market movements, analyze patterns, and get insights on emerging trends.",
    keywords: "prediction platform, trend analytics, market analysis, AI insights, data visualization, trend tracking, business intelligence, prediction analytics, trendphile analytics",
    ogImage: "https://trendphile.com/tp1logo-whatsapp.jpg",
    canonical: "https://trendphile.com/analytics",
    type: "website",
    robots: "index, follow"
  };

  // Apply SEO
  useSEO(seoConfig);

  // Structured data for SEO
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Trend Analytics - TrendPhile", 
    "description": "Advanced analytics and insights for trending topics and market analysis",
    "url": "https://trendphile.com/analytics"
  };
  useStructuredData(structuredData);
    const [dashboardData, setDashboardData] = useState(null);
    const [categoryActivityData, setCategoryActivityData] = useState(null);
    const [momentumData, setMomentumData] = useState(null);
    const [timingData, setTimingData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedTimeframe, setSelectedTimeframe] = useState('week');
    const [selectedCategory, setSelectedCategory] = useState('all');
    const navigate = useNavigate();
    const { isLoggedIn } = useAuth(); // Use auth context
    const [communityHealthData, setCommunityHealthData] = useState(null);
    const [qualityLengthData, setQualityLengthData] = useState(null);
    const scatterChartRef = useRef(null);

    


    // Refs for chart instances to handle cleanup
    const chartRefs = useRef({});
    const componentMounted = useRef(true);

    // Cleanup function for charts
    const destroyCharts = useCallback(() => {
        Object.values(chartRefs.current).forEach(chartRef => {
            if (chartRef && chartRef.current) {
                try {
                    chartRef.current.destroy();
                } catch (error) {
                    console.warn('Chart cleanup warning:', error);
                }
            }
        });
        chartRefs.current = {};
    }, []);

    useEffect(() => {
        componentMounted.current = true;
        return () => {
            componentMounted.current = false;
            destroyCharts();
        };
    }, [destroyCharts]);

    const generateChartColors = (count) => {
        const colors = [
            'rgba(255, 215, 0, 0.8)',    // Gold
            'rgba(255, 237, 78, 0.8)',   // Light Gold
            'rgba(218, 165, 32, 0.8)',   // Dark Gold
            'rgba(255, 193, 7, 0.8)',    // Amber
            'rgba(255, 223, 0, 0.8)',    // Golden Yellow
            'rgba(255, 206, 84, 0.8)',   // Light Amber
            'rgba(255, 183, 77, 0.8)',   // Orange Gold
            'rgba(255, 152, 0, 0.8)',    // Orange
            'rgba(255, 193, 158, 0.8)',  // Peach
            'rgba(255, 171, 145, 0.8)'   // Light Orange
        ];
        return colors.slice(0, count);
    };

    // Enhanced chart options with better styling
    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        animation: {
            duration: 1000,
            easing: 'easeInOutCubic'
        },
        plugins: {
            legend: {
                labels: {
                    color: '#ffffff',
                    font: {
                        size: 14,
                        family: 'Inter, system-ui, sans-serif',
                        weight: '500'
                    },
                    padding: 20,
                    usePointStyle: true,
                    pointStyle: 'circle'
                }
            },
            tooltip: {
                backgroundColor: 'rgba(0, 0, 0, 0.9)',
                titleColor: '#ffd700',
                bodyColor: '#ffffff',
                borderColor: '#ffd700',
                borderWidth: 1,
                cornerRadius: 12,
                displayColors: true,
                titleFont: {
                    size: 14,
                    weight: '600'
                },
                bodyFont: {
                    size: 13
                },
                padding: 12
            }
        },
        scales: {
            x: {
                ticks: {
                    color: '#cccccc',
                    font: {
                        size: 12,
                        family: 'Inter, system-ui, sans-serif'
                    }
                },
                grid: {
                    color: 'rgba(255, 255, 255, 0.08)',
                    lineWidth: 1
                },
                border: {
                    color: 'rgba(255, 255, 255, 0.1)'
                }
            },
            y: {
                ticks: {
                    color: '#cccccc',
                    font: {
                        size: 12,
                        family: 'Inter, system-ui, sans-serif'
                    }
                },
                grid: {
                    color: 'rgba(255, 255, 255, 0.08)',
                    lineWidth: 1
                },
                border: {
                    color: 'rgba(255, 255, 255, 0.1)'
                }
            }
        },
        elements: {
            point: {
                radius: 6,
                hoverRadius: 8,
                borderWidth: 2,
                backgroundColor: '#ffd700',
                borderColor: '#000000'
            },
            line: {
                tension: 0.4,
                borderWidth: 3
            }
        }
    };

    const doughnutOptions = {
        responsive: true,
        maintainAspectRatio: false,
        animation: {
            duration: 1000,
            easing: 'easeInOutCubic'
        },
        cutout: '60%', // Makes it more elegant
        plugins: {
            legend: {
                position: 'bottom',
                labels: {
                    color: '#ffffff',
                    padding: 20,
                    font: {
                        size: 12,
                        family: 'Inter, system-ui, sans-serif',
                        weight: '500'
                    },
                    usePointStyle: true,
                    pointStyle: 'circle'
                }
            },
            tooltip: {
                backgroundColor: 'rgba(0, 0, 0, 0.9)',
                titleColor: '#ffd700',
                bodyColor: '#ffffff',
                borderColor: '#ffd700',
                borderWidth: 1,
                cornerRadius: 12,
                padding: 12,
                callbacks: {
                    label: function(context) {
                        const label = context.label || '';
                        const value = context.parsed || 0;
                        const percentage = dashboardData?.categoryDistribution?.find(
                            item => item.category === label
                        )?.percentage || '0';
                        return `${label}: ${value} (${percentage}%)`;
                    }
                }
            }
        },
        elements: {
            arc: {
                borderWidth: 2,
                borderColor: 'rgba(0, 0, 0, 0.8)'
            }
        }
    };

    const fetchAnalyticsData = useCallback(async () => {
        if (!componentMounted.current) return;
        
        setLoading(true);
        setError(null);
        
        try {
            console.log(`Fetching analytics data - Timeframe: ${selectedTimeframe}, Category: ${selectedCategory}`);
            
            // Destroy existing charts before fetching new data
            destroyCharts();
            
            // Fetch main dashboard data
            const dashboardResponse = await axios.get(`${process.env.REACT_APP_API_URL}/analytics-dashboard`, {
                params: {
                    timeframe: selectedTimeframe,
                    category: selectedCategory
                }
            });
    
            if (!componentMounted.current) return;
    
            // Fetch all analytics data in parallel
            const [
                categoryActivityResponse, 
                momentumResponse, 
                timingResponse,
                communityHealthResponse,
                qualityLengthResponse
            ] = await Promise.all([
                axios.get(`${process.env.REACT_APP_API_URL}/analytics/category-activity`, {
                    params: { category: selectedCategory }
                }),
                axios.get(`${process.env.REACT_APP_API_URL}/analytics/momentum`, {
                    params: { category: selectedCategory }
                }),
                axios.get(`${process.env.REACT_APP_API_URL}/analytics/timing`, {
                    params: { category: selectedCategory }
                }),
                axios.get(`${process.env.REACT_APP_API_URL}/analytics/community-quality-health`, {
                    params: { category: selectedCategory !== 'all' ? selectedCategory : undefined }
                }),
                axios.get(`${process.env.REACT_APP_API_URL}/analytics/quality-vs-length`, {
                    params: { category: selectedCategory !== 'all' ? selectedCategory : undefined }
                })
            ]);
    
            if (!componentMounted.current) return;
    
            console.log('Analytics data received:', dashboardResponse.data);
            console.log('Community Health data:', communityHealthResponse.data);
            console.log('Quality vs Length data:', qualityLengthResponse.data);
            
            setDashboardData(dashboardResponse.data);
            setCategoryActivityData(categoryActivityResponse.data);
            setMomentumData(momentumResponse.data);
            setTimingData(timingResponse.data);
            setCommunityHealthData(communityHealthResponse.data);
            setQualityLengthData(qualityLengthResponse.data);
    
            // Create scatter plot after data is loaded
            if (qualityLengthResponse.data && componentMounted.current) {
                setTimeout(() => createScatterPlot(qualityLengthResponse.data), 100);
            }
    
        } catch (err) {
            if (!componentMounted.current) return;
            console.error('Error fetching analytics:', err);
            setError(err.response?.data?.message || 'Failed to fetch analytics data');
        } finally {
            if (componentMounted.current) {
                setLoading(false);
            }
        }
    }, [selectedTimeframe, selectedCategory, destroyCharts]);
    

    useEffect(() => {
        fetchAnalyticsData();
    }, [fetchAnalyticsData]);

    // Cleanup on timeframe/category change
    useEffect(() => {
        destroyCharts();
    }, [selectedTimeframe, selectedCategory, destroyCharts]);

    // Generate chart data from real API data with error handling
    const generateCategoryDistributionData = () => {
        try {
            if (!dashboardData?.categoryDistribution?.length) return null;

            const labels = dashboardData.categoryDistribution.map(item => item.category);
            const data = dashboardData.categoryDistribution.map(item => item.count);
            const colors = generateChartColors(labels.length);

            return {
                labels,
                datasets: [{
                    data,
                    backgroundColor: colors,
                    borderColor: colors.map(color => color.replace('0.8', '1')),
                    borderWidth: 2,
                    hoverOffset: 15,
                    hoverBorderWidth: 3
                }]
            };
        } catch (error) {
            console.warn('Error generating category distribution data:', error);
            return null;
        }
    };

    const generateUserCategoryDistributionData = () => {
        try {
            if (!dashboardData?.userCategoryDistribution?.length) return null;

            const labels = dashboardData.userCategoryDistribution.map(item => item.category);
            const data = dashboardData.userCategoryDistribution.map(item => item.user_count);
            const colors = generateChartColors(labels.length);

            return {
                labels,
                datasets: [{
                    data,
                    backgroundColor: colors,
                    borderColor: colors.map(color => color.replace('0.8', '1')),
                    borderWidth: 2,
                    hoverOffset: 15,
                    hoverBorderWidth: 3
                }]
            };
        } catch (error) {
            console.warn('Error generating user category distribution data:', error);
            return null;
        }
    };

    const generateWeeklyActivityData = () => {
        try {
            if (!dashboardData?.weeklyActivity) return null;

            return {
                labels: dashboardData.weeklyActivity.labels,
                datasets: [{
                    label: 'Predictions Created',
                    data: dashboardData.weeklyActivity.data,
                    backgroundColor: 'rgba(255, 215, 0, 0.15)',
                    borderColor: '#ffd700',
                    borderWidth: 3,
                    fill: true,
                    tension: 0.4,
                    pointBackgroundColor: '#ffd700',
                    pointBorderColor: '#000',
                    pointBorderWidth: 2,
                    pointRadius: 6,
                    pointHoverRadius: 8,
                    pointHoverBackgroundColor: '#ffed4e',
                    pointHoverBorderColor: '#000',
                    pointHoverBorderWidth: 3
                }]
            };
        } catch (error) {
            console.warn('Error generating weekly activity data:', error);
            return null;
        }
    };

    const generateDailyActivityData = () => {
        try {
            if (!dashboardData?.dailyActivity?.length) return null;

            const labels = dashboardData.dailyActivity.map(item => {
                const date = new Date(item.date);
                return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
            });
            const predictions = dashboardData.dailyActivity.map(item => item.predictions_count);
            const users = dashboardData.dailyActivity.map(item => item.unique_users);

            return {
                labels,
                datasets: [
                    {
                        label: 'Predictions',
                        data: predictions,
                        backgroundColor: 'rgba(255, 215, 0, 0.15)',
                        borderColor: '#ffd700',
                        borderWidth: 3,
                        fill: true,
                        tension: 0.4,
                        pointBackgroundColor: '#ffd700',
                        pointBorderColor: '#000',
                        pointBorderWidth: 2,
                        pointRadius: 5,
                        pointHoverRadius: 7
                    },
                    {
                        label: 'Active Users',
                        data: users,
                        backgroundColor: 'rgba(255, 152, 0, 0.15)',
                        borderColor: '#ff9800',
                        borderWidth: 3,
                        fill: true,
                        tension: 0.4,
                        pointBackgroundColor: '#ff9800',
                        pointBorderColor: '#000',
                        pointBorderWidth: 2,
                        pointRadius: 5,
                        pointHoverRadius: 7
                    }
                ]
            };
        } catch (error) {
            console.warn('Error generating daily activity data:', error);
            return null;
        }
    };
    // Generate Community Health donut chart data
const generateCommunityHealthData = () => {
    try {
        if (!communityHealthData?.data?.length) return null;

        const labels = communityHealthData.data.map(item => item.tier);
        const data = communityHealthData.data.map(item => item.count);
        
        // Quality-based colors
        const qualityColors = {
            'High Quality': 'rgba(76, 175, 80, 0.8)',      // Green
            'Good Quality': 'rgba(255, 193, 7, 0.8)',      // Amber  
            'Fair Quality': 'rgba(255, 152, 0, 0.8)',      // Orange
            'Needs Improvement': 'rgba(244, 67, 54, 0.8)'  // Red
        };

        const colors = labels.map(label => qualityColors[label] || 'rgba(158, 158, 158, 0.8)');

        return {
            labels,
            datasets: [{
                data,
                backgroundColor: colors,
                borderColor: colors.map(color => color.replace('0.8', '1')),
                borderWidth: 2,
                hoverOffset: 15,
                hoverBorderWidth: 3
            }]
        };
    } catch (error) {
        console.warn('Error generating community health data:', error);
        return null;
    }
};

// Create scatter plot using Canvas API
const createScatterPlot = (data) => {
    if (!scatterChartRef.current || !data?.data?.length) return;

    const canvas = scatterChartRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    
    // Set canvas size
    canvas.width = rect.width * window.devicePixelRatio;
    canvas.height = rect.height * window.devicePixelRatio;
    ctx.scale(window.devicePixelRatio, window.devicePixelRatio);
    
    const width = rect.width;
    const height = rect.height;
    const padding = 60;
    
    // Clear canvas
    ctx.fillStyle = '#1a1a1a';
    ctx.fillRect(0, 0, width, height);
    
    // Get data ranges
    const xValues = data.data.map(d => d.x);
    const yValues = data.data.map(d => d.y);
    const xMin = Math.min(...xValues);
    const xMax = Math.max(...xValues);
    const yMin = Math.min(...yValues);
    const yMax = Math.max(...yValues);
    
    // Scale functions
    const scaleX = (x) => padding + (x - xMin) / (xMax - xMin) * (width - padding * 2);
    const scaleY = (y) => height - padding - (y - yMin) / (yMax - yMin) * (height - padding * 2);
    
    // Draw grid
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.1)';
    ctx.lineWidth = 1;
    for (let i = 0; i <= 10; i++) {
        const x = padding + (width - padding * 2) * i / 10;
        const y = padding + (height - padding * 2) * i / 10;
        
        ctx.beginPath();
        ctx.moveTo(x, padding);
        ctx.lineTo(x, height - padding);
        ctx.stroke();
        
        ctx.beginPath();
        ctx.moveTo(padding, y);
        ctx.lineTo(width - padding, y);
        ctx.stroke();
    }
    
    // Draw axes
    ctx.strokeStyle = 'rgba(255, 215, 0, 0.5)';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(padding, height - padding);
    ctx.lineTo(width - padding, height - padding);
    ctx.moveTo(padding, padding);
    ctx.lineTo(padding, height - padding);
    ctx.stroke();
    
    // Draw points
    data.data.forEach(point => {
        const x = scaleX(point.x);
        const y = scaleY(point.y);
        
        // Color based on tier
        let color = '#ffd700';
        if (point.tier === 'High Quality') color = '#4caf50';
        else if (point.tier === 'Good Quality') color = '#ffc107';
        else if (point.tier === 'Fair Quality') color = '#ff9800';
        else if (point.tier === 'Needs Improvement') color = '#f44336';
        
        ctx.fillStyle = color;
        ctx.beginPath();
        ctx.arc(x, y, 4, 0, 2 * Math.PI);
        ctx.fill();
        
        // Add subtle glow
        ctx.shadowColor = color;
        ctx.shadowBlur = 8;
        ctx.beginPath();
        ctx.arc(x, y, 2, 0, 2 * Math.PI);
        ctx.fill();
        ctx.shadowBlur = 0;
    });
    
    // Draw labels
    ctx.fillStyle = '#cccccc';
    ctx.font = '12px Inter, sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText('Text Length (characters)', width / 2, height - 20);
    
    ctx.save();
    ctx.translate(20, height / 2);
    ctx.rotate(-Math.PI / 2);
    ctx.fillText('Reliability Score', 0, 0);
    ctx.restore();
};



    // Chart wrapper component with error boundary
    const ChartWrapper = ({ children, chartId }) => {
        const chartRef = useRef(null);
        
        useEffect(() => {
            chartRefs.current[chartId] = chartRef;
            return () => {
                if (chartRefs.current[chartId]) {
                    delete chartRefs.current[chartId];
                }
            };
        }, [chartId]);

        return (
            <div ref={chartRef} className="chart-wrapper">
                {children}
            </div>
        );
    };

    // Loading component
    if (loading) {
        return (
            <div className="analytics-container">
                <div className="loading-spinner">
                    <div className="spinner"></div>
                    <p>Loading Real-Time Analytics...</p>
                </div>
            </div>
        );
    }

    // Error component
    if (error) {
        return (
            <div className="analytics-container">
                <div className="error-message">
                    <AlertCircle size={48} className="error-icon" />
                    <h3>Analytics Unavailable</h3>
                    <p>{error}</p>
                    <button onClick={fetchAnalyticsData} className="retry-button">
                        <RefreshCw size={16} />
                        Try Again
                    </button>
                </div>
            </div>
        );
    }

    // No data component
    if (!dashboardData || !dashboardData.success) {
        return (
            <div className="analytics-container">
                <div className="no-data-message">
                    <Database size={48} className="no-data-icon" />
                    <h3>No Data Available</h3>
                    <p>No prediction data found for the selected filters.</p>
                    <button onClick={() => {
                        setSelectedTimeframe('month');
                        setSelectedCategory('all');
                    }} className="reset-filters-button">
                        <RefreshCw size={16} />
                        Reset Filters
                    </button>
                </div>
            </div>
        );
    }

    const categoryDistributionChart = generateCategoryDistributionData();
    const userCategoryChart = generateUserCategoryDistributionData();
    const weeklyActivityChart = generateWeeklyActivityData();
    const dailyActivityChart = generateDailyActivityData();
    return (
        <div className="analytics-container">
            {/* Login prompt for non-logged users */}
            {!isLoggedIn && (
                <div className="public-notice animate-fade-in">
                    <div className="notice-content">
                        <Eye size={20} className="notice-icon" />
                        <span>You're viewing public analytics. <button onClick={() => navigate('/login')} className="login-link">Login</button> to access personalized insights!</span>
                    </div>
                </div>
            )}
    
            {/* Hero Section */}
            <div className="analytics-hero">
                <div className="hero-content">
                    <h1 className="analytics-title">
                        <BarChart3 size={48} className="title-icon" />
                        Analytics Dashboard
                    </h1>
                    <p className="analytics-subtitle">
                        Real-time insights from {dashboardData.metrics.totalPredictions} predictions across {dashboardData.metrics.categoriesActive} categories
                    </p>
                    <div className="analytics-summary">
                        <span className="summary-item">
                            <Calendar size={16} />
                            {selectedTimeframe === 'week' ? 'Last 7 days' : 
                                selectedTimeframe === 'month' ? 'Last 30 days' : 
                                selectedTimeframe === 'quarter' ? 'Last 3 months' : 'Last year'}
                        </span>
                        {selectedCategory !== 'all' && (
                            <span className="summary-item">
                                <Tag size={16} />
                                {selectedCategory}
                            </span>
                        )}
                    </div>
                </div>
                <div className="analytics-filters">
                    <div className="filter-group">
                        <label><Calendar size={16} /> Timeframe:</label>
                        <select 
                            value={selectedTimeframe} 
                            onChange={(e) => setSelectedTimeframe(e.target.value)}
                            className="filter-select"
                        >
                            <option value="week">This Week</option>
                            <option value="month">This Month</option>
                            <option value="quarter">This Quarter</option>
                            <option value="year">This Year</option>
                        </select>
                    </div>
                    <div className="filter-group">
                        <label><Tag size={16} /> Category:</label>
                        <select 
                            value={selectedCategory} 
                            onChange={(e) => setSelectedCategory(e.target.value)}
                            className="filter-select"
                        >
                            <option value="all">All Categories</option>
                            <option value="Technology">Technology</option>
                            <option value="Politics">Politics</option>
                            <option value="Economy">Economy</option>
                            <option value="Sports">Sports</option>
                            <option value="Entertainment">Entertainment</option>
                            <option value="Healthcare">Healthcare</option>
                            <option value="Environment">Environment</option>
                            <option value="Society">Society</option>
                        </select>
                    </div>
                </div>
            </div>
    
            {/* AI Technology Section */}
            <div className="ai-technology-section">
                <div className="ai-banner">
                    <div className="ai-banner-content">
                        <div className="ai-icon">
                            <Settings size={48} />
                        </div>
                        <div className="ai-text">
                            <h3>Powered by Advanced AI & Machine Learning</h3>
                            <p>Our analytics platform leverages state-of-the-art Artificial Intelligence, Machine Learning algorithms, and Deep Learning neural networks to deliver highly accurate predictive insights with precision rates exceeding 94%.</p>
                        </div>
                    </div>
                    <div className="ai-features">
                        <div className="ai-feature">
                            <span className="ai-feature-icon"><Brain size={24} /></span>
                            <span>Deep Learning Models</span>
                        </div>
                        <div className="ai-feature">
                            <span className="ai-feature-icon"><TrendingUp size={24} /></span>
                            <span>Predictive Analytics</span>
                        </div>
                        <div className="ai-feature">
                            <span className="ai-feature-icon"><Target size={24} /></span>
                            <span>94%+ Accuracy</span>
                        </div>
                        <div className="ai-feature">
                            <span className="ai-feature-icon"><Zap size={24} /></span>
                            <span>Real-time Processing</span>
                        </div>
                    </div>
                </div>
            </div>
    
            {/* Key Metrics */}
            <div className="metrics-grid">
                <div className="metric-card">
                    <div className="metric-icon">
                        <Target size={32} />
                    </div>
                    <div className="metric-content">
                        <h3>{dashboardData.metrics.totalPredictions.toLocaleString()}</h3>
                        <p>Total Predictions</p>
                        <span className="metric-change positive">
                            <Globe size={12} />
                            Live data
                        </span>
                    </div>
                </div>
                <div className="metric-card">
                    <div className="metric-icon">
                        <Zap size={32} />
                    </div>
                    <div className="metric-content">
                        <h3>{dashboardData.metrics.avgPredictionsPerDay}</h3>
                        <p>Avg. Predictions/Day</p>
                        <span className="metric-change positive">
                            <Activity size={12} />
                            Based on {dashboardData.summary.totalDaysAnalyzed} days
                        </span>
                    </div>
                </div>
                <div className="metric-card">
                    <div className="metric-icon">
                        <Users size={32} />
                    </div>
                    <div className="metric-content">
                        <h3>{dashboardData.metrics.activeUsers}</h3>
                        <p>Active Users</p>
                        <span className="metric-change neutral">
                            <Clock size={12} />
                            In selected timeframe
                        </span>
                    </div>
                </div>
                <div className="metric-card">
                    <div className="metric-icon">
                        <BarChart3 size={32} />
                    </div>
                    <div className="metric-content">
                        <h3>{dashboardData.metrics.categoriesActive}</h3>
                        <p>Active Categories</p>
                        <span className="metric-change positive">
                            <Sparkles size={12} />
                            With predictions
                        </span>
                    </div>
                </div>
            </div>
    
{/* Charts Grid - COMPLETELY REMOVE DOUGHNUT CHARTS WHEN CATEGORY SELECTED */}
<div className="charts-grid">
    {/* Only render doughnut charts when ALL categories are selected */}
    {selectedCategory === 'all' && categoryDistributionChart && (
        <div className="chart-container chart-switching-in">
            <div className="chart-header">
                <PieChart size={24} className="chart-header-icon" />
                <div>
                    <h3>Category Distribution</h3>
                    <p>{dashboardData.categoryDistribution.length} categories with predictions</p>
                </div>
            </div>
            <ChartWrapper chartId="categoryDistribution">
                <Doughnut 
                    data={categoryDistributionChart} 
                    options={doughnutOptions}
                    key={`category-${selectedTimeframe}-${selectedCategory}`}
                />
            </ChartWrapper>
        </div>
    )}

    {selectedCategory === 'all' && userCategoryChart && (
        <div className="chart-container chart-switching-in">
            <div className="chart-header">
                <Users size={24} className="chart-header-icon" />
                <div>
                    <h3>Users by Category</h3>
                    <p>How many users predicted in each category</p>
                </div>
            </div>
            <ChartWrapper chartId="userCategory">
                <Doughnut 
                    data={userCategoryChart} 
                    options={doughnutOptions}
                    key={`users-${selectedTimeframe}-${selectedCategory}`}
                />
            </ChartWrapper>
        </div>
    )}

    {/* Weekly Activity Pattern - Always show */}
    {weeklyActivityChart && (
        <div className="chart-container chart-switching-in">
            <div className="chart-header">
                <Calendar size={24} className="chart-header-icon" />
                <div>
                    <h3>Weekly Activity Pattern</h3>
                    <p>Predictions by day of week ({dashboardData.weeklyActivity.total} total)</p>
                </div>
            </div>
            <ChartWrapper chartId="weeklyActivity">
                <Line 
                    data={weeklyActivityChart} 
                    options={chartOptions}
                    key={`weekly-${selectedTimeframe}-${selectedCategory}`}
                />
            </ChartWrapper>
        </div>
    )}

    {/* Daily Activity Timeline - Always show */}
    {dailyActivityChart && (
        <div className="chart-container chart-switching-in">
            <div className="chart-header">
                <LineChart size={24} className="chart-header-icon" />
                <div>
                    <h3>Daily Activity Timeline</h3>
                    <p>Daily predictions and active users over time</p>
                </div>
            </div>
            <ChartWrapper chartId="dailyActivity">
                <Line 
                    data={dailyActivityChart} 
                    options={chartOptions}
                    key={`daily-${selectedTimeframe}-${selectedCategory}`}
                />
            </ChartWrapper>
        </div>
    )}

    {/* 🆕 Community Quality Health - Donut Chart */}
    <div className="chart-container chart-switching-in">
        <div className="chart-header">
            <Gauge size={24} className="chart-header-icon" />
            <div>
                <h3>Community Quality Health</h3>
                <p>Distribution of prediction quality tiers (Overall Score: {communityHealthData?.summary?.overall_health_score || 'Loading...'})</p>
            </div>
        </div>
        <ChartWrapper chartId="communityHealth">
            {communityHealthData ? (
                <Doughnut 
                    data={generateCommunityHealthData()} 
                    options={{
                        ...doughnutOptions,
                        plugins: {
                            ...doughnutOptions.plugins,
                            tooltip: {
                                ...doughnutOptions.plugins.tooltip,
                                callbacks: {
                                    label: function(context) {
                                        const label = context.label || '';
                                        const value = context.parsed || 0;
                                        const item = communityHealthData.data.find(d => d.tier === label);
                                        return `${label}: ${value} predictions (${item?.percentage || 0}%)`;
                                    }
                                }
                            }
                        }
                    }}
                    key={`community-health-${selectedTimeframe}-${selectedCategory}`}
                />
            ) : (
                <div className="chart-loading">
                    <div className="loading-dots"></div>
                    <span>Analyzing community health...</span>
                </div>
            )}
        </ChartWrapper>
    </div>

    {/* 🆕 Quality vs Text Length Scatter Plot */}
    <div className="chart-container chart-switching-in chart-wide">
        <div className="chart-header">
            <Target size={24} className="chart-header-icon" />
            <div>
                <h3>Quality vs Text Length Analysis</h3>
                <p>Correlation: {qualityLengthData?.statistics?.correlation_interpretation || 'Loading...'} 
                   ({qualityLengthData?.statistics?.data_points || 0} predictions)</p>
            </div>
        </div>
        <ChartWrapper chartId="qualityLength">
            {qualityLengthData ? (
                <div className="scatter-chart-container">
                    <canvas ref={scatterChartRef} width="400" height="300"></canvas>
                    <div className="correlation-stats">
                        <div className="stat-pill">
                            <span className="stat-label">Correlation:</span>
                            <span className="stat-value">{(qualityLengthData.statistics.correlation * 100).toFixed(1)}%</span>
                        </div>
                        <div className="stat-pill">
                            <span className="stat-label">Data Points:</span>
                            <span className="stat-value">{qualityLengthData.statistics.data_points}</span>
                        </div>
                        <div className="stat-pill">
                            <span className="stat-label">Avg Length:</span>
                            <span className="stat-value">{qualityLengthData.statistics.avg_text_length} chars</span>
                        </div>
                    </div>
                </div>
            ) : (
                <div className="chart-loading">
                    <div className="loading-dots"></div>
                    <span>Analyzing quality patterns...</span>
                </div>
            )}
        </ChartWrapper>
    </div>
</div>


    
            {/* AI-Powered Real-Time Insights */}
            <div className="insights-section">
                <h3 className="insights-title">
                    <Cpu size={32} className="insights-title-icon" />
                    AI-Powered Real-Time Insights
                </h3>
                <div className="insights-grid">
                    {/* Category Activity Score */}
                    <div className="insight-card activity-score">
                        <div className="insight-header">
                            <div className="insight-icon">
                                <BarChart3 size={32} />
                            </div>
                            <div className="insight-info">
                                <h4>Category Activity Score</h4>
                                <div className="insight-badge">
                                    <Globe size={12} />
                                    Live Data
                                </div>
                            </div>
                        </div>
                        <div className="insight-content">
                            {categoryActivityData ? (
                                <>
                                    <div className="insight-value-wrapper">
                                        <span className="insight-value">{categoryActivityData.activityScore}%</span>
                                        <span className="insight-label">Activity Rate</span>
                                    </div>
                                    <p className="insight-description">
                                        <strong>{categoryActivityData.categoryName}</strong> {categoryActivityData.description.toLowerCase()}
                                    </p>
                                    <div className="insight-stats">
                                        <span className="stat-item">
                                            <span className="stat-number">{categoryActivityData.categoryPredictions.toLocaleString()}</span>
                                            <span className="stat-label">Predictions</span>
                                        </span>
                                        <ChevronRight size={16} className="stat-divider" />
                                        <span className="stat-item">
                                            <span className="stat-number">{categoryActivityData.totalPredictions.toLocaleString()}</span>
                                            <span className="stat-label">Total</span>
                                        </span>
                                    </div>
                                </>
                            ) : (
                                <div className="insight-loading">
                                    <div className="loading-dots"></div>
                                    <span>Analyzing category activity...</span>
                                </div>
                            )}
                        </div>
                    </div>
    
                    {/* Category Momentum */}
                    <div className="insight-card momentum">
                        <div className="insight-header">
                            <div className="insight-icon">
                                <TrendingUp size={32} />
                            </div>
                            <div className="insight-info">
                                <h4>Category Momentum</h4>
                                <div className="insight-badge trending">
                                    <Activity size={12} />
                                    Trending
                                </div>
                            </div>
                        </div>
                        <div className="insight-content">
                            {momentumData ? (
                                <>
                                    <div className="insight-value-wrapper">
                                        <span className="insight-value">{momentumData.growthRate > 0 ? '+' : ''}{momentumData.growthRate}%</span>
                                        <span className="insight-label">Growth Rate</span>
                                    </div>
                                    <p className="insight-description">
                                        <strong>{momentumData.momentumText}</strong>
                                    </p>
                                    <div className="insight-stats">
                                        <span className="stat-item">
                                            <span className="stat-number">{momentumData.recentCount}</span>
                                            <span className="stat-label">This Month</span>
                                        </span>
                                        <ChevronRight size={16} className="stat-divider" />
                                        <span className="stat-item">
                                            <span className="stat-number">{momentumData.previousCount}</span>
                                            <span className="stat-label">Last Month</span>
                                        </span>
                                    </div>
                                </>
                            ) : (
                                <div className="insight-loading">
                                    <div className="loading-dots"></div>
                                    <span>Calculating momentum...</span>
                                </div>
                            )}
                        </div>
                    </div>
    
                    {/* Prediction Timing Patterns */}
                    <div className="insight-card timing">
                        <div className="insight-header">
                            <div className="insight-icon">
                                <Clock size={32} />
                            </div>
                            <div className="insight-info">
                                <h4>Prediction Timing Patterns</h4>
                                <div className="insight-badge patterns">
                                    <Gauge size={12} />
                                    Patterns
                                </div>
                            </div>
                        </div>
                        <div className="insight-content">
                            {timingData ? (
                                <>
                                    <div className="insight-value-wrapper">
                                        <span className="insight-value">{timingData.peakHourFormatted}</span>
                                        <span className="insight-label">Peak Hour</span>
                                    </div>
                                    <p className="insight-description">
                                        <strong>Peak activity: {timingData.peakDay}s</strong> with {timingData.primaryPeriod} preference
                                    </p>
                                    <div className="insight-stats">
                                        <span className="stat-item">
                                            <span className="stat-number">{timingData.weekdayVsWeekend.weekdayPercentage}%</span>
                                            <span className="stat-label">Weekdays</span>
                                        </span>
                                        <ChevronRight size={16} className="stat-divider" />
                                        <span className="stat-item">
                                            <span className="stat-number">{100 - timingData.weekdayVsWeekend.weekdayPercentage}%</span>
                                            <span className="stat-label">Weekends</span>
                                        </span>
                                    </div>
                                </>
                            ) : (
                                <div className="insight-loading">
                                    <div className="loading-dots"></div>
                                    <span>Analyzing timing patterns...</span>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}    

export default Analytics;
